import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { api } from '../lib/api.js'
import { addDays, formatDate } from '../lib/dates.js'
import Modal from './Modal.jsx'
import { DateField } from './DatePicker.jsx'

/**
 * The one genuinely destructive action in the product, so it is built to be un-surprising:
 * real counts fetched before anything happens, an export offered first, a typed confirmation
 * when rows would be deleted, and plain language about what survives.
 */
export default function ModeSwitchDialog({ target, today, defaultLabel, onClose, onDone }) {
  const [timing, setTiming] = useState('date') // 'immediate' | 'date'
  const [effectiveFrom, setEffectiveFrom] = useState(addDays(today, 7))
  const [label, setLabel] = useState(target === 'cat' ? defaultLabel || '' : '')
  const [clearFuture, setClearFuture] = useState(false)
  const [confirm, setConfirm] = useState('')
  const [previews, setPreviews] = useState({ immediate: null, date: null })
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  // Immediate ALWAYS clears — that is what the option means. A future-dated switch keeps the
  // outgoing schedule by default, and it comes back when the company switches back.
  const willClear = timing === 'immediate' ? true : clearFuture
  const boundary = timing === 'immediate' ? today : effectiveFrom
  const { modeName } = useAuth()
  const outgoing = modeName(target === 'cat' ? 'normal' : 'cat')
  const incoming = modeName(target)

  // Both options are previewed, not just the selected one: the owner has to be able to
  // compare "clears 46" against "clears 18" before choosing, which is the entire reason this
  // dialog shows numbers at all.
  useEffect(() => {
    let alive = true
    setError(null)
    const ask = (from) =>
      api.post('/api/owner/mode-periods/preview', {
        mode: target,
        effectiveFrom: from,
        clearOutgoing: true,
      })
    Promise.all([ask('immediate'), ask(effectiveFrom)])
      .then(([immediate, date]) => alive && setPreviews({ immediate, date }))
      .catch((err) => alive && setError(err.message))
    return () => { alive = false }
  }, [target, effectiveFrom])

  const preview = previews[timing]
  const affected = preview?.affectedCount ?? 0
  const needsTyping = willClear && affected > 0
  const canGo = !busy && !error && (!needsTyping || confirm.toUpperCase() === 'CLEAR')

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/owner/mode-periods', {
        mode: target,
        effectiveFrom: timing === 'immediate' ? 'immediate' : effectiveFrom,
        label: label || null,
        clearOutgoing: willClear,
        confirm: needsTyping ? 'CLEAR' : undefined,
      })
      onDone()
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <Modal title={`Switch to ${incoming} mode`} onClose={onClose} width={640}>
        {error && <div className="error">{error}</div>}

        {target === 'cat' && (
          <div className="field">
            <label>Name this activation (optional)</label>
            <input type="text" value={label} placeholder="Aug 2026 Windstorm"
              onChange={(e) => setLabel(e.target.value)} />
          </div>
        )}

        <label className={`radio-block ${timing === 'immediate' ? 'selected' : ''}`}>
          <div className="rb-title">
            <input type="radio" checked={timing === 'immediate'} onChange={() => setTiming('immediate')} />
            Effective immediately
          </div>
          <div className="rb-body">
            Starts today, {formatDate(today)}. The {outgoing} schedule from today onward is{' '}
            <strong>cleared</strong>.
            {previews.immediate?.affectedCount > 0 && (
              <> <span className="count-badge">{previews.immediate.affectedCount} entries will be deleted</span></>
            )}
          </div>
        </label>

        <label className={`radio-block ${timing === 'date' ? 'selected' : ''}`}>
          <div className="rb-title">
            <input type="radio" checked={timing === 'date'} onChange={() => setTiming('date')} />
            Starts on a specific date
            <span style={{ marginLeft: 8 }}>
              <DateField
                value={effectiveFrom}
                min={today}
                today={today}
                label="Switch takes effect on"
                onChange={(iso) => { setTiming('date'); setEffectiveFrom(iso) }}
              />
            </span>
          </div>
          <div className="rb-body">
            {outgoing} mode runs until {formatDate(addDays(effectiveFrom, -1))}. The {outgoing}{' '}
            schedule is kept — it reappears when you switch back.
            <div style={{ marginTop: 6 }}>
              <label className="row" style={{ gap: 6 }}>
                <input type="checkbox" checked={clearFuture} disabled={timing !== 'date'}
                  onChange={(e) => setClearFuture(e.target.checked)} />
                Also clear the {outgoing} schedule from {effectiveFrom} onward
                {previews.date?.affectedCount > 0 && (
                  <span className={clearFuture ? 'count-badge' : 'tag muted'}>
                    {previews.date.affectedCount} entries
                  </span>
                )}
              </label>
            </div>
          </div>
        </label>

        <div className="notice">
          {/* Nothing before the boundary is ever touched — yesterday's board is history. */}
          Nothing before {boundary} is touched, in either case.
          {target === 'cat' && preview && (
            <> {modeName('cat')} teams: {preview.teamsConfigured} configured · {preview.catStaffAssigned} staff assigned.</>
          )}
        </div>

        {willClear && affected > 0 && (
          <>
            <div className="row">
              <a className="btn" href={`/api/export/schedule.csv?mode=${preview?.outgoingMode}&from=${boundary}&to=${addDays(boundary, 60)}`}>
                Export {outgoing} schedule as CSV first
              </a>
            </div>
            <div className="field" style={{ marginTop: 12 }}>
              <label>Type CLEAR to confirm deleting {affected} entries</label>
              <input type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="CLEAR" />
              <span className="hint">
                Deleted entries are written to the audit log in full and can be reconstructed.
              </span>
            </div>
          </>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!canGo} onClick={submit}>
            {busy ? 'Switching…' : `Switch to ${incoming}`}
          </button>
        </div>
    </Modal>
  )
}
